import React, { useState, useEffect, useRef } from 'react';
import { api } from '../lib/api';

interface FournisseurAutocompleteProps {
  entrepriseId: string;
  value: string | null;
  onChange: (id: string | null) => void;
  displayName?: string | null;
}

interface FournisseurItem {
  id: string;
  nom: string;
  email?: string | null;
  siret?: string | null;
  telephone?: string | null;
}

const FournisseurAutocomplete: React.FC<FournisseurAutocompleteProps> = ({ entrepriseId, value, onChange, displayName }) => {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<FournisseurItem[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedName, setSelectedName] = useState(displayName || '');

  useEffect(() => {
    setSelectedName(displayName || '');
  }, [displayName]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createNom, setCreateNom] = useState('');
  const [createEmail, setCreateEmail] = useState('');
  const [createSiret, setCreateSiret] = useState('');
  const [createTelephone, setCreateTelephone] = useState('');
  const [creating, setCreating] = useState(false);
  const debounceRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (search.length < 1) {
      setResults([]);
      setShowDropdown(false);
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const data = await api.fournisseurs.list(entrepriseId, search);
        setResults(data);
        setShowDropdown(true);
      } catch {}
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [search, entrepriseId]);

  const handleSelect = (f: FournisseurItem) => {
    onChange(f.id);
    setSelectedName(f.nom);
    setSearch('');
    setShowDropdown(false);
  };

  const handleClear = () => {
    onChange(null);
    setSelectedName('');
    setSearch('');
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      const newFournisseur = await api.fournisseurs.create({
        entreprise_id: entrepriseId,
        nom: createNom,
        email: createEmail || undefined,
        siret: createSiret || undefined,
        telephone: createTelephone || undefined,
      });
      handleSelect(newFournisseur);
      setShowCreateDialog(false);
      setCreateNom('');
      setCreateEmail('');
      setCreateSiret('');
      setCreateTelephone('');
    } catch {}
    setCreating(false);
  };

  const hasExactMatch = results.some(r => r.nom.toLowerCase() === search.toLowerCase());

  const inputClass = 'w-full rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400';

  if (value && selectedName) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 text-sm">
        <span className="flex-1 truncate">{selectedName}</span>
        <button type="button" onClick={handleClear} className="text-gray-400 hover:text-gray-600">&times;</button>
      </div>
    );
  }

  return (
    <div className="relative">
      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        onFocus={() => search.length >= 1 && setShowDropdown(true)}
        onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
        placeholder="Rechercher un fournisseur..."
        className={inputClass}
      />
      {showDropdown && (
        <ul role="listbox" className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
          {results.map((f) => (
            <li
              key={f.id}
              role="option"
              aria-selected={false}
              onMouseDown={() => handleSelect(f)}
              className="px-4 py-2 text-sm hover:bg-red-50 cursor-pointer border-b border-gray-50 last:border-0"
            >
              {f.nom}
            </li>
          ))}
          {(!hasExactMatch || results.length < 1) && search.length >= 1 && (
            <li
              onMouseDown={() => { setCreateNom(search); setShowCreateDialog(true); }}
              className="px-4 py-2 text-sm text-red-600 hover:bg-red-50 cursor-pointer font-medium"
            >
              + Créer &quot;{search}&quot;
            </li>
          )}
        </ul>
      )}

      {showCreateDialog && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm">
            <h3 className="text-lg font-semibold mb-4">Créer un fournisseur</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-500">Nom</label>
                <input type="text" value={createNom} onChange={(e) => setCreateNom(e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className="text-xs text-gray-500">Email</label>
                <input type="email" value={createEmail} onChange={(e) => setCreateEmail(e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className="text-xs text-gray-500">SIRET</label>
                <input type="text" value={createSiret} onChange={(e) => setCreateSiret(e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className="text-xs text-gray-500">Téléphone</label>
                <input type="tel" value={createTelephone} onChange={(e) => setCreateTelephone(e.target.value)} className={inputClass} />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button type="button" onClick={() => setShowCreateDialog(false)} className="flex-1 py-2 rounded-xl border text-sm">Annuler</button>
              <button type="button" onClick={handleCreate} disabled={creating || !createNom} className="flex-1 py-2 rounded-xl bg-red-600 text-white text-sm font-medium disabled:opacity-40">
                {creating ? '...' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FournisseurAutocomplete;
